/** Minimal key-value store; localStorage when usable, memory otherwise. */
export interface KVStore {
  get(key: string): string | null;
  /** False when the write was dropped (storage full). */
  set(key: string, value: string): boolean;
  remove(key: string): void;
}

class MemoryStore implements KVStore {
  private data = new Map<string, string>();

  get(key: string): string | null {
    return this.data.get(key) ?? null;
  }

  set(key: string, value: string): boolean {
    this.data.set(key, value);
    return true;
  }

  remove(key: string): void {
    this.data.delete(key);
  }
}

/**
 * Returns a localStorage-backed store, falling back to memory when
 * localStorage is unavailable or blocked (SSR, private mode, disabled
 * cookies). Writes never throw — quota errors are swallowed so tracking
 * cannot break the host page.
 */
export function createStore(): KVStore {
  try {
    const ls = window.localStorage;
    const probe = '__narrato_probe__';
    ls.setItem(probe, '1');
    ls.removeItem(probe);
    return {
      get: (key) => ls.getItem(key),
      set: (key, value) => {
        try {
          ls.setItem(key, value);
          return true;
        } catch {
          // Quota exceeded — drop the write rather than throw into app code.
          return false;
        }
      },
      remove: (key) => ls.removeItem(key),
    };
  } catch {
    return new MemoryStore();
  }
}
